import {useEffect, useState} from 'react';
import {view, Action, Icon, useCollapsible} from '@sail/ui';
import {CollapsibleContainer} from '@sail/ui/deprecated';
import {useAnalytics} from '@stripe-internal/inline-data';
import {defineMessages, useIntl} from 'react-intl';
import {useNewUniversalHelpDesign} from '@stripe-internal/conversations-ui';
import {LiveAnswersSource} from '../UniversalHelpLiveAnswersResponse';
import {sourcesClosedActionEvent, sourcesOpenedActionEvent} from '../analytics';
import {SourceLink} from './SourcesLink';

const messages = defineMessages({
  viewSources: {
    id: 'mse_inline_data.universal_help_live_answers_response.sources_dropdown.view_sources',
    description: 'Label to expand live answers sources dropdown',
    defaultMessage:
      '{sourcesCount, plural, one {View 1 source} other {View # sources}}',
  },
  hideSources: {
    id: 'mse_inline_data.universal_help_live_answers_response.sources_dropdown.hide_sources',
    description: 'Label to collapse live answers sources dropdown',
    defaultMessage:
      '{sourcesCount, plural, one {Hide 1 source} other {Hide # sources}}',
  },
});

export const SourcesDropdown = ({
  sources,
}: {
  sources: Array<LiveAnswersSource>;
}) => {
  const useNewDesign = useNewUniversalHelpDesign();
  const analytics = useAnalytics();
  const intl = useIntl();

  const [isOpen, setOpen] = useState(false);
  const [markedOpen, setMarkedOpen] = useState(false);
  const {triggerProps, collapsibleProps, icon} = useCollapsible({
    open: isOpen,
    iconOrientation: 'right-down',
  });

  useEffect(() => {
    if (isOpen) {
      analytics.action(sourcesOpenedActionEvent, {});
      setMarkedOpen(true);
    } else if (markedOpen) {
      analytics.action(sourcesClosedActionEvent, {});
      setMarkedOpen(false);
    }
  }, [isOpen, analytics, markedOpen]);

  const rows = sources.map((source, i) => (
    <SourceLink
      source={source}
      // eslint-disable-next-line react/no-array-index-key
      key={`live-answers-source-${i}`}
    />
  ));

  return (
    <view.div>
      <Action
        onPress={() => {
          setOpen(!isOpen);
        }}
        {...triggerProps}
        css={{
          stack: 'x',
          alignX: 'start',
          alignY: 'center',
          gap: 'small',
          color: 'action.primary',
          font: 'label.small.emphasized',
        }}
      >
        <Icon icon={icon} size="xxsmall" css={{color: 'inherit'}} />
        {intl.formatMessage(
          isOpen ? messages.hideSources : messages.viewSources,
          {sourcesCount: sources.length},
        )}
      </Action>
      <CollapsibleContainer {...collapsibleProps}>
        <view.div
          css={{
            stack: 'y',
            // The new design has a default line height unlike the old design, so this needs to be adjusted
            gap: useNewDesign ? 'small' : 'medium',
            paddingY: 'medium',
          }}
        >
          {rows}
        </view.div>
      </CollapsibleContainer>
    </view.div>
  );
};
